import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import classnames from 'classnames';
import './index.scss';

import {WAITER_TYPES} from '../../../utils/enums';
import {createAction} from '../../../utils/sagaHelper';
import {actionUpdatePortalMapData} from '../../../store/reducers/portalMap/portalMap-actions';
import {objectKeysToUpperLowerCase} from '../../../utils/objects-util';
import {REGION_TEMPLATE_SETTING_CREATE_REGIONS} from '../../../store/reducers/restaurant/restaurant-actions';
import {fetchRegionTemplateCollections, fetchRegionTemplates, fetchRegionTemplatesAtLocation} from '../../../api/restaurant-requests';
import {getControlCenters} from '../../../services/restaurant';

import LayoutControl from '../LayoutControl';
import PortalServiceMap from '../PortalMap';
import SearchBox from '../SearchBox';
import CheckBox from '../CheckBox';
import Dropdown from '../Dropdown';
import IconButton from '../IconButton';
import Waiter from '../Waiter/Waiter';
import { minutesToTime } from '../../../utils/convertTime';

// the collection payload is not guaranteed to carry the amount of regions, so the first
// known value wins and the exact one is cached as soon as a collection has been opened
const COUNT_ACCESSORS = ['regionsCount', 'templatesCount', 'regionsAmount', 'templatesAmount', 'count'];

// a missing collection or an empty collection answers with 404, which is an empty list here
const getData = requestResult => {
    const data = requestResult && requestResult.status === 200 ? objectKeysToUpperLowerCase(requestResult.data) : null;
    return Array.isArray(data) ? data : [];
};

const ChooseRegionsFromTemplate = ({open, onClose}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const regions = useSelector(state => state.restaurant.data.configuration.regions);
    const regionIds = regions.map(r => r.geoRegionTemplateId).filter(Boolean);

    const [collections, setCollections] = useState([]);
    const [collectionsInProcess, setCollectionsInProcess] = useState(false);
    const [collectionsSearch, setCollectionsSearch] = useState('');
    const [collection, setCollection] = useState(null);

    const [templates, setTemplates] = useState([]);
    const [templatesInProcess, setTemplatesInProcess] = useState(false);
    const [templatesSearch, setTemplatesSearch] = useState('');
    const [templatesCount, setTemplatesCount] = useState({});

    const [selectedIds, setSelectedIds] = useState([]);
    const [showSelected, setShowSelected] = useState(false);
    const [locationInProcess, setLocationInProcess] = useState(false);
    // the viewport follows the selection made in the list, a selection made on the map keeps it where it is
    const [boundsBySelection, setBoundsBySelection] = useState(true);
    const [maxDeliveryTime, setMaxDeliveryTime] = useState(null);
    const [defaultControlCenterId, setDefaultControlCenterId] = useState(null);

    // only the answer of the last click counts, the earlier ones may belong to another collection
    const locationRequestRef = useRef(0);
    // the map keeps the handler it has been mounted with, so the click reaches the current data through a ref
    const mapClickRef = useRef(null);

    useEffect(() => {
        if (!open) {
            dispatch(actionUpdatePortalMapData({isInit: true}));
            return;
        }

        setCollectionsSearch('');
        setCollection(null);
        setTemplates([]);
        setTemplatesSearch('');
        setSelectedIds([]);
        setShowSelected(false);
        setLocationInProcess(false);
        setBoundsBySelection(true);
        locationRequestRef.current += 1;
        setMaxDeliveryTime(null);
        setDefaultControlCenterId(null);
        setCollectionsInProcess(true);

        fetchRegionTemplateCollections().then(
            requestResult => {
                setCollections(getData(requestResult));
                setCollectionsInProcess(false);
            },
            () => {
                setCollections([]);
                setCollectionsInProcess(false);
            }
        );
        // eslint-disable-next-line
    }, [open]);

    const controlCenters = [{key: null, value: '-'}].concat(
        getControlCenters().map(controlCenter => ({key: controlCenter.controlCenterId, value: controlCenter.name}))
    );

    const filteredCollections = collections.filter(item =>
        (item.collectionName || '').toLowerCase().includes(collectionsSearch.toLowerCase())
    );
    const filteredTemplates = templates.filter(item =>
        (item.name || '').toLowerCase().includes(templatesSearch.toLowerCase()) &&
        (!showSelected || selectedIds.includes(item.id))
    );
    // the templates already used by an existing region are locked, so they are out of the select all scope
    const selectableTemplates = templates.filter(item => !regionIds.includes(item.id));
    const isAllSelected = !!selectableTemplates.length && selectableTemplates.every(item => selectedIds.includes(item.id));

    const getCollectionCount = item => {
        const cached = templatesCount[item.id];
        if (Number.isFinite(cached)) return cached;

        const value = COUNT_ACCESSORS.map(accessor => item[accessor]).find(count => Number.isFinite(count));
        return Number.isFinite(value) ? value : null;
    };

    // the shared map takes polygons as { id, region: [locations] }
    const selectedRegions = templates
        .filter(item => selectedIds.includes(item.id))
        .map(item => {
            const deliveryRegion = item.deliveryRegion || {};
            const locations = Array.isArray(deliveryRegion) ? deliveryRegion : deliveryRegion.region;
            return {id: item.id, region: locations || []};
        })
        .filter(item => item.region.length);

    const handleOnClose = () => {
        return new Promise(resolve => {
            onClose && onClose();
            resolve();
        });
    };

    const handleOnSelectCollection = item => {
        setCollection(item);
        setTemplates([]);
        setTemplatesSearch('');
        setSelectedIds([]);
        setShowSelected(false);
        setLocationInProcess(false);
        setBoundsBySelection(true);
        locationRequestRef.current += 1;
        setTemplatesInProcess(true);

        fetchRegionTemplates(item.id).then(
            requestResult => {
                const data = getData(requestResult);
                setTemplates(data);
                setTemplatesCount(counts => ({...counts, [item.id]: data.length}));
                setTemplatesInProcess(false);
            },
            () => {
                setTemplates([]);
                setTemplatesInProcess(false);
            }
        );
    };

    const handleOnSelectTemplate = (id, value) => {
        setBoundsBySelection(true);
        setSelectedIds(ids => (value ? ids.concat(id) : ids.filter(f => f !== id)));
    };

    // the point answers with every template covering it, the ones outside of the opened collection
    // have no checkbox to reflect them, so they are left out. A click toggles the templates it hits:
    // the ones not selected yet are added, the ones already selected are removed
    const handleOnSelectAtLocation = clickLocation => {
        const {lat, lng} = clickLocation || {};
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const requestId = locationRequestRef.current + 1;
        locationRequestRef.current = requestId;
        setLocationInProcess(true);

        fetchRegionTemplatesAtLocation(lat, lng).then(
            requestResult => {
                if (locationRequestRef.current !== requestId) return;
                setLocationInProcess(false);

                const locationIds = getData(requestResult).map(item => item.id);
                const ids = selectableTemplates.filter(item => locationIds.includes(item.id)).map(item => item.id);
                if (!ids.length) return;

                setBoundsBySelection(false);
                setSelectedIds(selected =>
                    selected.filter(id => !ids.includes(id)).concat(ids.filter(id => !selected.includes(id)))
                );
            },
            () => {
                if (locationRequestRef.current !== requestId) return;
                setLocationInProcess(false);
            }
        );
    };

    useEffect(() => {
        mapClickRef.current = handleOnSelectAtLocation;
    });

    const handleOnMapClick = useCallback(clickLocation => {
        if (mapClickRef.current) mapClickRef.current(clickLocation);
    }, []);

    // the parameter goes to the server as a number, an empty field means it is not applied
    const handleOnChangeMaxDeliveryTime = value => {
        const number = Number(value);
        setMaxDeliveryTime(value === '' || Number.isNaN(number) ? null : number);
    };

    const handleOnSelectAll = value => {
        setBoundsBySelection(true);
        setSelectedIds(value ? selectableTemplates.map(item => item.id) : []);
    };

    const handleOnDelectAll = value => {
        setBoundsBySelection(true);
        setSelectedIds([]);
    };

    const handleOnCreateRegions = () => {
        if (!selectedIds.length) return Promise.resolve();

        return dispatch(
            createAction(REGION_TEMPLATE_SETTING_CREATE_REGIONS, {
                templateIds: selectedIds,
                maxDeliveryTime: minutesToTime(maxDeliveryTime),
                defaultControlCenterId: defaultControlCenterId,
            })
        ).then(() => {
            onClose && onClose();
        });
    };

    return (
        <LayoutControl open={open} onClose={onClose} component={(
            <div className={'chooseRegionsFromTemplate'}>
                <div className={'templatesHeader'}>
                    <div className={'templatesTitle'}>{t('settings.regionTemplates.LABEL')}</div>
                    <IconButton isIcon={true}
                        iconName={'cancel'}
                        onClick={handleOnClose}
                    />
                </div>
                <div className={'templatesContent'}>
                    <div className={'templatesColumn'}>
                        <div className={'columnTitle'}>{t('settings.regionTemplates.COLLECTIONS_LABEL')}</div>
                        <SearchBox useValue={collectionsSearch}
                            placeholder={t('settings.regionTemplates.SEARCH_PLACEHOLDER')}
                            debounce={300}
                            onChange={value => setCollectionsSearch(value || '')}
                        />
                        <div className={'columnList'}>
                            {collectionsInProcess && (
                                <Waiter inProcess={true} type={WAITER_TYPES.buttonDiv} />
                            )}
                            {!collectionsInProcess && !filteredCollections.length && (
                                <div className={'columnEmpty'}>{t('settings.regionTemplates.EMPTY_COLLECTIONS_LABEL')}</div>
                            )}
                            {!collectionsInProcess && filteredCollections.map(item => {
                                const count = getCollectionCount(item);
                                return (
                                    <div key={`regionTemplateCollection-${item.id}`}
                                        className={classnames('collectionItem', {selected: collection && collection.id === item.id})}
                                        onClick={() => handleOnSelectCollection(item)}
                                    >
                                        <div className={'collectionName'}>{item.collectionName}</div>
                                        {count !== null && (<div className={'collectionCount'}>{count}</div>)}
                                        <IconButton isIcon={true}
                                            iconName={'angleUp'}
                                            onClick={() => handleOnSelectCollection(item)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className={classnames('templatesColumn', {wideColumn: !collection})}>
                        {!collection && (
                            <div className={'columnPlaceholder'}>
                                <div className={'placeholderIcon'} />
                                <div className={'placeholderText'}>
                                    {t('settings.regionTemplates.SELECT_COLLECTION_LABEL')}
                                </div>
                            </div>
                        )}
                        {collection && (
                            <>
                                <div className={'templatesColumnHeader'}>
                                    <div className={'columnTitle'}>{collection.collectionName}</div>
                                    {!templatesInProcess && !!templates.length && (
                                        <CheckBox value={showSelected}
                                            label={t('settings.regionTemplates.SHOW_SELECTED_LABEL')}
                                            onChange={setShowSelected}
                                        />
                                    )}
                                </div>
                                <SearchBox useValue={templatesSearch}
                                    placeholder={t('settings.regionTemplates.SEARCH_PLACEHOLDER')}
                                    debounce={300}
                                    onChange={value => setTemplatesSearch(value || '')}
                                />
                                {!templatesInProcess && !!selectableTemplates.length && (
                                    <div className={'selectAllItem'}>
                                        <CheckBox value={isAllSelected}
                                            label={`${t('settings.regionTemplates.SELECT_ALL_LABEL')} (${selectableTemplates.length})`}
                                            onChange={handleOnSelectAll}
                                        />
                                        {!!selectedIds.length && (
                                            <div className='deselectBtn' onClick={handleOnDelectAll}>{t("settings.regionTemplates.DELECT_ALL_LABEL")}</div>
                                        )}
                                    </div>
                                )}
                                <div className={'columnList'}>
                                    {templatesInProcess && (
                                        <Waiter inProcess={true} type={WAITER_TYPES.buttonDiv} />
                                    )}
                                    {!templatesInProcess && !templates.length && (
                                        <div className={'columnEmpty'}>{t('settings.regionTemplates.EMPTY_REGIONS_LABEL')}</div>
                                    )}
                                    {!templatesInProcess && !!templates.length && showSelected && !filteredTemplates.length && (
                                        <div className={'columnEmpty'}>{t('settings.regionTemplates.EMPTY_SELECTED_LABEL')}</div>
                                    )}
                                    {!templatesInProcess && filteredTemplates.map(item => (
                                        <div key={`regionTemplate-${item.id}`}
                                            className={classnames('templateItem', {selected: selectedIds.includes(item.id), disabled: regionIds.includes(item.id)})}
                                        >
                                            <CheckBox value={selectedIds.includes(item.id)}
                                                label={item.name}
                                                disabled={regionIds.includes(item.id)}
                                                onChange={value => handleOnSelectTemplate(item.id, value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    {!!collection && (
                        <div className={classnames('templatesColumn', 'previewColumn')}>
                            <div className={'columnTitle'}>{t('settings.regionTemplates.PREVIEW_LABEL')}</div>
                            <div className={'previewMap'}>
                                {open && (
                                    <PortalServiceMap
                                        markers={[]}
                                        regions={selectedRegions}
                                        isReadonly={true}
                                        alwaysShowPolygons={true}
                                        boundsByRegions={boundsBySelection}
                                        handleOnMapClick={handleOnMapClick}
                                    />
                                )}
                                {locationInProcess && (
                                    <div className={'previewMapWaiter'}>
                                        <Waiter inProcess={true} type={WAITER_TYPES.buttonDiv} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <div className={'templatesParameters'}>
                    <div className={'parametersTitle'}>
                        <span className={'parametersTitleText'}>{t('settings.regionTemplates.COMMON_PARAMETERS_LABEL')}</span>
                        <span className={'parametersTitleDescription'}>{t('settings.regionTemplates.COMMON_PARAMETERS_DESCRIPTION')}</span>
                    </div>
                    <div className={'parametersFields'}>
                        <div className={'parametersField'}>
                            <div className={'fieldTitle'}>
                                {`${t('settings.regionTemplates.MAX_DELIVERY_TIME_LABEL')}, ${t('settings.map.TIME_MEASURE')}`}
                            </div>
                            <input type={'number'}
                                min={'0'}
                                value={maxDeliveryTime === null ? '' : maxDeliveryTime}
                                onChange={event => handleOnChangeMaxDeliveryTime(event.target.value)}
                            />
                        </div>
                        <div className={'parametersField'}>
                            <div className={'fieldTitle'}>{t('settings.regionTemplates.DEFAULT_CONTROL_CENTER_LABEL')}</div>
                            <Dropdown value={(controlCenters.find(f => f.key === defaultControlCenterId) || {}).value}
                                values={controlCenters}
                                onSelect={item => setDefaultControlCenterId(item.key)}
                            />
                        </div>
                    </div>
                </div>
                <div className={'templatesControls'}>
                    <Waiter type={WAITER_TYPES.button}
                        useClass={'btn denied'}
                        stopPropagation={true}
                        spanText={t('basic.CANCEL_BUTTON')}
                        handleOnClick={handleOnClose}
                    />
                    <Waiter type={WAITER_TYPES.button}
                        useClass={'btn confirm'}
                        stopPropagation={true}
                        spanText={t('settings.regionTemplates.SELECT_REGIONS_BUTTON', {count: selectedIds.length})}
                        disabled={!selectedIds.length}
                        handleOnClick={handleOnCreateRegions}
                    />
                </div>
            </div>
        )}/>
    );
};

export default ChooseRegionsFromTemplate;
